/**
 * What each section of the drawer can change.
 *
 * One component per control, each taking the detail and a way to say the order moved. They
 * are separate from `sections.tsx` on purpose: a section knows how to lay a fact out and
 * nothing about how to change one, which is what let the read-only drawer ship first and
 * this land on top of it without touching a single line of layout.
 *
 * None of these is optimistic and none of them shows a generic toast on failure. The
 * server's problem+json detail renders next to the control that caused it, because on this
 * screen the message is usually the instruction: "Void the label in ShipStation first" is
 * something to go and do, not something to apologise for.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Address,
  HoldReason,
  LineEditDiff,
  LineEditRequest,
} from '@elutide/shared';
import type { OrderDrawerDetailView as OrderDrawerDetail } from '../types';
import {
  Button,
  ConfirmDialog,
  SelectField,
  TextArea,
  TextInput,
  Toggle,
  useToast,
} from '@/components';
import { formatMoney } from '@/lib/format';
import { ordersApi } from '../api';
import { CARRIER_OPTIONS, detectCarrier } from '../carrier';
import { centsToInput, parseDollars } from '../money';
import { ActionPanel, ActionProblem, useAction } from './feedback';

export interface SectionActionProps {
  detail: OrderDrawerDetail;
  onChanged: () => void;
}

/* ------------------------------------------------------------------ */
/* Payment: extend and remind                                          */
/* ------------------------------------------------------------------ */

export function PaymentActions({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [confirmRemind, setConfirmRemind] = useState(false);
  const p = detail.payment;

  if (!p.autocancel_extendable && !p.reminder_eligible) return null;

  return (
    <div className="ord-sec__actions">
      {p.autocancel_extendable ? (
        <Button
          size="sm"
          busy={action.busy}
          onClick={() =>
            void action
              .run(() => ordersApi.extendAutoCancel(detail.header.order_id, { note: null }))
              .then((ok) => {
                if (ok) {
                  onChanged();
                  toast.push({ tone: 'ok', message: 'Auto-cancel pushed out.' });
                }
              })
          }
        >
          Extend
        </Button>
      ) : null}

      {p.reminder_eligible ? (
        <Button size="sm" variant="ghost" onClick={() => setConfirmRemind(true)}>
          Send reminder
        </Button>
      ) : null}

      <ActionProblem state={action} />

      <ConfirmDialog
        open={confirmRemind}
        title={`Remind ${detail.customer.name} to pay`}
        body="Sends the next due payment reminder now."
        effects={[
          'An email leaves the building immediately.',
          'Quiet hours and the per customer daily cap still apply, so it may be skipped.',
          'The send is recorded on the order.',
        ]}
        confirmLabel="Send it"
        onCancel={() => setConfirmRemind(false)}
        onConfirm={async () => {
          setConfirmRemind(false);
          await action.run(async () => {
            const result = await ordersApi.sendReminder(detail.header.order_id, 'email');
            toast.push({
              tone: result.sent ? 'ok' : 'warn',
              message: result.sent
                ? 'Reminder sent.'
                : `Not sent: ${result.skipped_reason ?? 'suppressed'}.`,
            });
            onChanged();
          });
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fulfilment: tracking                                                */
/* ------------------------------------------------------------------ */

export function TrackingEditor({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState(detail.fulfilment.tracking_number ?? '');
  const [carrier, setCarrier] = useState<string>(detail.fulfilment.carrier);
  const [touchedCarrier, setTouchedCarrier] = useState(false);
  const [notify, setNotify] = useState(false);

  /**
   * Autodetect, until the operator says otherwise.
   *
   * A pasted 1Z is UPS and nobody should have to say so. Two things stop it being clever
   * at the wrong moment: it does not fire for the number the form opened with, so a carrier
   * somebody set by hand on an existing shipment survives merely opening the form, and it
   * stops entirely once the select has been touched, because that is exactly the case where
   * the guess is the thing being corrected.
   */
  const openedWith = useRef(detail.fulfilment.tracking_number ?? '');
  useEffect(() => {
    if (touchedCarrier) return;
    if (number.trim() === '' || number === openedWith.current) return;
    setCarrier(detectCarrier(number));
  }, [number, touchedCarrier]);

  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {detail.fulfilment.tracking_number ? 'Edit tracking' : 'Add tracking'}
      </Button>
    );
  }

  return (
    <ActionPanel title="Tracking" onClose={() => setOpen(false)}>
      <div className="ord-form">
        <TextInput
          label="Tracking number"
          mono
          required
          value={number}
          onChange={(event) => setNumber(event.target.value)}
        />
        <SelectField
          label="Carrier"
          hint={touchedCarrier ? 'Set by hand.' : 'Detected from the number.'}
          value={carrier}
          options={CARRIER_OPTIONS}
          onChange={(event) => {
            setTouchedCarrier(true);
            setCarrier(event.target.value);
          }}
        />
        <Toggle checked={notify} label="Email the customer the tracking" onChange={setNotify} />
      </div>

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          busy={action.busy}
          disabled={number.trim().length < 4}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.updateTracking(detail.header.order_id, {
                  carrier,
                  tracking_number: number.trim(),
                  notify_customer: notify,
                }),
              )
              .then((ok) => {
                if (ok) {
                  setOpen(false);
                  onChanged();
                  toast.push({ tone: 'ok', message: 'Tracking saved.' });
                }
              })
          }
        >
          Save tracking
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Items: edit lines, with the diff first                              */
/* ------------------------------------------------------------------ */

export function LineEditor({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState<Record<number, number>>({});
  const [reason, setReason] = useState('');
  const [diff, setDiff] = useState<LineEditDiff | null>(null);

  const reset = useCallback(() => {
    setQty({});
    setReason('');
    setDiff(null);
    action.reset();
  }, [action]);

  const ops = useMemo<LineEditRequest['ops']>(
    () =>
      detail.items.lines
        .filter((line) => qty[line.line_id] !== undefined && qty[line.line_id] !== line.qty)
        .map((line) => ({
          op: 'set_qty' as const,
          line_id: line.line_id,
          qty: qty[line.line_id] as number,
        })),
    [detail.items.lines, qty],
  );

  const body = useMemo<LineEditRequest>(
    () =>
      ({
        expected_version: detail.header.version,
        ops,
        shipping_cents: null,
        coupon_add: null,
        coupon_remove: null,
        reason: reason.trim() === '' ? 'line edit' : reason.trim(),
        allow_oversell: false,
      }) as LineEditRequest,
    [detail.header.version, ops, reason],
  );

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          reset();
          setOpen(true);
        }}
      >
        Edit lines
      </Button>
    );
  }

  return (
    <ActionPanel
      title="Edit lines"
      onClose={() => {
        setOpen(false);
        reset();
      }}
    >
      <ul className="ord-lineedit" role="list">
        {detail.items.lines.map((line) => {
          const current = qty[line.line_id] ?? line.qty;
          return (
            <li key={line.line_id}>
              <span className="ord-name">{line.name}</span>
              <span className="ord-lineedit__qty">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`One fewer ${line.name}`}
                  disabled={current === 0}
                  onClick={() => {
                    setDiff(null);
                    setQty((c) => ({ ...c, [line.line_id]: Math.max(0, current - 1) }));
                  }}
                >
                  -
                </Button>
                <span className="o-mono" aria-live="polite">
                  {current}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`One more ${line.name}`}
                  onClick={() => {
                    setDiff(null);
                    setQty((c) => ({ ...c, [line.line_id]: current + 1 }));
                  }}
                >
                  +
                </Button>
              </span>
            </li>
          );
        })}
      </ul>

      <TextArea
        label="Reason"
        required
        rows={2}
        value={reason}
        onChange={(event) => {
          setDiff(null);
          setReason(event.target.value);
        }}
      />

      {diff ? (
        <div className="ord-diff">
          <h5 className="o-section-title">What will change</h5>
          <div className="o-kv">
            <span className="o-kv__key">Total</span>
            <span className="o-kv__value">
              {formatMoney(diff.before.total_cents, { showCents: true })} to{' '}
              <strong>{formatMoney(diff.after.total_cents, { showCents: true })}</strong>
            </span>
          </div>
          {diff.balance_due_cents > 0 ? (
            <div className="o-kv">
              <span className="o-kv__key">Balance due</span>
              <span className="o-kv__value ord-variance">
                {formatMoney(diff.balance_due_cents, { showCents: true })}
              </span>
            </div>
          ) : null}
          {diff.overpaid_cents > 0 ? (
            <div className="o-kv">
              <span className="o-kv__key">Overpaid</span>
              <span className="o-kv__value ord-variance">
                {formatMoney(diff.overpaid_cents, { showCents: true })}
              </span>
            </div>
          ) : null}
          {diff.stock_deltas.length > 0 ? (
            <div className="o-kv">
              <span className="o-kv__key">Stock</span>
              <span className="o-kv__value">
                {diff.stock_deltas
                  .map((entry) => `${entry.delta > 0 ? '+' : ''}${entry.delta} on ${entry.product_id}`)
                  .join(', ')}
              </span>
            </div>
          ) : null}
          <p className="ord-sub">
            Committing returns the order to paid, clears the slip and certificate acks and
            marks it for reprint.
          </p>
        </div>
      ) : null}

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        {diff ? (
          <Button
            variant="primary"
            busy={action.busy}
            onClick={() =>
              void action
                .run(() => ordersApi.commitLines(detail.header.order_id, body))
                .then((ok) => {
                  if (ok) {
                    setOpen(false);
                    reset();
                    onChanged();
                    toast.push({ tone: 'ok', message: 'Lines updated.' });
                  }
                })
            }
          >
            Commit the change
          </Button>
        ) : (
          <Button
            busy={action.busy}
            disabled={ops.length === 0 || reason.trim().length === 0}
            onClick={() =>
              void action.run(async () => {
                setDiff(await ordersApi.previewLines(detail.header.order_id, body));
              })
            }
          >
            Preview
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Customer: address                                                   */
/* ------------------------------------------------------------------ */

const EMPTY_ADDRESS: Address = {
  first_name: '',
  last_name: '',
  company: null,
  address_1: '',
  address_2: null,
  city: '',
  state: '',
  postcode: '',
  country: 'US',
};

export function AddressEditor({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState<Address>(detail.customer.shipping ?? EMPTY_ADDRESS);
  const [alsoBilling, setAlsoBilling] = useState(false);
  const [reason, setReason] = useState('');

  const field = (key: keyof Address, label: string, required = false) => (
    <TextInput
      label={label}
      required={required}
      value={(address[key] as string | null) ?? ''}
      onChange={(event) =>
        setAddress((current) => ({ ...current, [key]: event.target.value }) as Address)
      }
    />
  );

  if (!open) {
    return (
      <Button
        size="sm"
        variant="ghost"
        disabled={!detail.customer.address_editable}
        title={
          detail.customer.address_editable
            ? undefined
            : 'The carrier already has this parcel.'
        }
        onClick={() => {
          setAddress(detail.customer.shipping ?? EMPTY_ADDRESS);
          setReason('');
          action.reset();
          setOpen(true);
        }}
      >
        Edit address
      </Button>
    );
  }

  return (
    <ActionPanel title="Edit address" onClose={() => setOpen(false)}>
      <div className="ord-form ord-form--address">
        {field('first_name', 'First name', true)}
        {field('last_name', 'Last name', true)}
        {field('address_1', 'Address', true)}
        {field('address_2', 'Address line 2')}
        {field('city', 'City', true)}
        {field('state', 'State', true)}
        {field('postcode', 'ZIP', true)}
        {field('country', 'Country', true)}
      </div>
      <Toggle
        checked={alsoBilling}
        label="Apply to the billing address too"
        onChange={setAlsoBilling}
      />
      <TextArea
        label="Reason"
        hint="Audited with a redacted diff: which fields changed, plus city, state and country."
        required
        rows={2}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          busy={action.busy}
          disabled={reason.trim().length === 0 || address.address_1.trim() === ''}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.editAddress(detail.header.order_id, {
                  expected_version: detail.header.version,
                  shipping: address,
                  ...(alsoBilling ? { billing: address } : {}),
                  reason: reason.trim(),
                } as never),
              )
              .then((ok) => {
                if (ok) {
                  setOpen(false);
                  onChanged();
                  toast.push({ tone: 'ok', message: 'Address updated.' });
                }
              })
          }
        >
          Save the address
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/* ------------------------------------------------------------------ */
/* Customer: Research Rewards                                          */
/* ------------------------------------------------------------------ */

export function CreditGrant({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [custom, setCustom] = useState('');
  const [reason, setReason] = useState('');
  const guest = detail.customer.customer_key === '';

  const grant = useCallback(
    (cents: number) =>
      void action
        .run(() =>
          ordersApi.credit(detail.header.order_id, {
            amount_cents: cents,
            reason:
              reason.trim() === ''
                ? `Granted from order ${detail.header.order_number}`
                : reason.trim(),
          }),
        )
        .then((ok) => {
          if (ok) {
            setCustom('');
            setReason('');
            onChanged();
            toast.push({
              tone: 'ok',
              message: `${formatMoney(cents, { showCents: true })} Research Rewards granted.`,
            });
          }
        }),
    [action, detail, onChanged, reason, toast],
  );

  const customCents = parseDollars(custom);

  return (
    <div className="ord-credit">
      <span className="o-kv__key">Grant credit</span>
      <div className="ord-credit__row">
        <Button size="sm" busy={action.busy} disabled={guest} onClick={() => grant(1000)}>
          +$10
        </Button>
        <Button size="sm" busy={action.busy} disabled={guest} onClick={() => grant(2000)}>
          +$20
        </Button>
        <input
          className="o-input ord-credit__input"
          inputMode="decimal"
          aria-label="Custom credit amount in dollars"
          placeholder="Custom"
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
        />
        <Button
          size="sm"
          busy={action.busy}
          disabled={guest || customCents === null || customCents <= 0}
          onClick={() => customCents !== null && grant(customCents)}
        >
          Grant
        </Button>
      </div>
      <TextInput
        label="Reason"
        hint="Optional. Shown on the customer's rewards ledger."
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      {guest ? <p className="ord-sub">This is a guest order, so there is no balance to credit.</p> : null}
      <ActionProblem state={action} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Notes                                                               */
/* ------------------------------------------------------------------ */

export function NoteComposer({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [customerVisible, setCustomerVisible] = useState(false);

  return (
    <div className="ord-note-form">
      <TextArea
        label="Add a note"
        rows={2}
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      <div className="ord-form__actions">
        <Toggle
          checked={customerVisible}
          label="Customer visible"
          onChange={setCustomerVisible}
        />
        <Button
          size="sm"
          busy={action.busy}
          disabled={body.trim().length === 0}
          onClick={() =>
            void action
              .run(() =>
                ordersApi.addNote(detail.header.order_id, body.trim(), customerVisible),
              )
              .then((ok) => {
                if (ok) {
                  setBody('');
                  setCustomerVisible(false);
                  onChanged();
                  // No undo. A customer visible note has already emailed the customer and
                  // an internal one is already in the Woo order history; offering to take
                  // it back would be a lie.
                  toast.push({ tone: 'ok', message: 'Note added.' });
                }
              })
          }
        >
          {customerVisible ? 'Send to the customer' : 'Add note'}
        </Button>
      </div>
      {customerVisible ? (
        <p className="ord-sub">
          A customer visible note emails the customer through WooCommerce. There is no undo.
        </p>
      ) : null}
      <ActionProblem state={action} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Flags: compliance hold                                              */
/* ------------------------------------------------------------------ */

const HOLD_REASONS: readonly { value: HoldReason; label: string }[] = [
  { value: 'identity_verification', label: 'Identity verification' },
  { value: 'restricted_destination', label: 'Restricted destination' },
  { value: 'quantity_threshold', label: 'Quantity threshold' },
  { value: 'payment_verification', label: 'Payment verification' },
  { value: 'suspected_fraud', label: 'Suspected fraud' },
  { value: 'other', label: 'Other' },
];

export function HoldControl({ detail, onChanged }: SectionActionProps) {
  const action = useAction();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<HoldReason>('identity_verification');
  const [note, setNote] = useState('');

  const held = detail.flags.some((flag) => flag.flag === 'compliance_hold');

  if (!open) {
    return (
      <Button size="sm" variant={held ? 'primary' : 'ghost'} onClick={() => setOpen(true)}>
        {held ? 'Clear hold' : 'Place hold'}
      </Button>
    );
  }

  return (
    <ActionPanel title={held ? 'Clear the hold' : 'Place a hold'} onClose={() => setOpen(false)}>
      <div className="ord-form">
        {held ? null : (
          <SelectField
            label="Reason"
            value={reason}
            options={HOLD_REASONS}
            onChange={(event) => setReason(event.target.value as HoldReason)}
          />
        )}
        <TextArea
          label={held ? 'Why it is being cleared' : 'Note'}
          required={held}
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <ActionProblem state={action} />

      <div className="ord-form__actions">
        <Button
          busy={action.busy}
          disabled={held && note.trim().length === 0}
          onClick={() =>
            void action
              .run(() =>
                held
                  ? ordersApi.releaseHold(detail.header.order_id, note.trim())
                  : ordersApi.placeHold(
                      detail.header.order_id,
                      reason,
                      note.trim() === '' ? null : note.trim(),
                    ),
              )
              .then((ok) => {
                if (ok) {
                  setOpen(false);
                  setNote('');
                  onChanged();
                  toast.push({ tone: 'ok', message: held ? 'Hold cleared.' : 'Hold placed.' });
                }
              })
          }
        >
          {held ? 'Clear the hold' : 'Place the hold'}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Close
        </Button>
      </div>
    </ActionPanel>
  );
}

/** Not used by the drawer directly; exported so a test can reach the money helper. */
export { centsToInput };
